import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader2, Pencil, Plus, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { UnderlineTabs } from '../components/ui/underline-tabs';
import { useEvent } from '../contexts/EventContext';
import { useRealtime } from '../contexts/RealtimeContext';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';

interface Category {
  id: string;
  name: string;
}

interface Team {
  id: string;
  team_id: string; // code d'équipe = dossard (LOG-172)
  name: string;
  category_id?: string | null;
}

interface RaceResult {
  id: string;
  team_id: string; // code
  team_name: string;
  bib_number?: string;
  category_id?: string | null;
  real_time_seconds: number | null;
  // LOG-162 : temps final = brut + pénalités validées ; DQ hors classement
  penalty_seconds?: number;
  is_dq?: boolean;
  elapsed_time?: number | null;
  manual_override_at?: string | null;
}

interface ResultChange {
  id: string;
  team_id: string;
  team_name: string;
  field: string;
  old_value: number | null;
  new_value: number | null;
  reason: string | null;
  origin: 'cloud_admin' | 'organizer' | 'sync';
  changed_by: string | null;
  created_at: string;
}

type View = 'ranking' | 'history';

// « 49:35 », « 1:02:48 », « 49:35.5 » → secondes. null si illisible.
const parseTime = (value: string): number | null => {
  const m = value.trim().match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})(?:[.,](\d{1,2}))?$/);
  if (!m) return null;
  const hours = Number(m[1] || 0);
  const minutes = Number(m[2]);
  const seconds = Number(m[3]);
  if (minutes > 59 || seconds > 59) return null;
  const fraction = m[4] ? Number(`0.${m[4]}`) : 0;
  const total = hours * 3600 + minutes * 60 + seconds + fraction;
  return total > 0 ? total : null;
};

const formatTime = (seconds: number | null | undefined) => {
  if (seconds === null || seconds === undefined || seconds < 0) return '--:--';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(secs).padStart(2, '0')}`;
};

const DIGITAL = "font-['DS-Digital'] tabular-nums tracking-wide";

const EventRaceResults: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const { setSelectedEventId } = useEvent();

  // Cale le dropdown global sur l'événement de l'URL.
  useEffect(() => {
    if (eventId) setSelectedEventId(eventId);
  }, [eventId, setSelectedEventId]);

  const [view, setView] = useState<View>('ranking');
  const [categories, setCategories] = useState<Category[]>([]);
  // Un classement se lit toujours DANS une catégorie, jamais entre catégories
  // (règle actée le 2026-09-02). Vide tant que les catégories ne sont pas
  // chargées, puis la première.
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [results, setResults] = useState<RaceResult[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [changes, setChanges] = useState<ResultChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Feuille d'édition
  const [editing, setEditing] = useState<{ team: Team; current: RaceResult | null } | null>(null);
  const [timeInput, setTimeInput] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('');

  const loadMetadata = useCallback(async () => {
    if (!eventId) return;
    try {
      // LOG-166 : apiService (intercepteur refresh token) au lieu de fetch brut
      const [catsRes, teamsRes] = await Promise.all([
        apiService.get('/categories', { event_id: eventId }),
        apiService.getTeams(eventId),
      ]);
      const cats: Category[] = catsRes.data.categories || [];
      setCategories(cats);
      setSelectedCategory((current) => (cats.some((c) => c.id === current) ? current : (cats[0]?.id ?? '')));
      setTeams(teamsRes.data.teams || []);
    } catch (err) {
      console.error('Erreur chargement métadonnées:', err);
    }
  }, [eventId]);

  const loadResults = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const [resultsRes, changesRes] = await Promise.all([
        apiService.get(`/events/${eventId}/race-results`),
        apiService.get(`/events/${eventId}/race-results/changes`),
      ]);
      setResults(resultsRes.data.results || []);
      setChanges(changesRes.data.changes || []);
    } catch (err: any) {
      setError(err.message || t('results.loadError'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    setSelectedCategory('');
    loadMetadata();
    loadResults();
  }, [eventId, loadMetadata, loadResults]);

  // Temps réel (LOG-201) : une arrivée enregistrée par le buzzer ou un tapis
  // RFID — ou une correction faite ailleurs — atterrit ici sans rechargement.
  useRealtime(['race_result'], () => { loadResults(); });

  // Dernière correction par équipe : pour le survol de la marque « corrigé ».
  const lastChangeByTeam = useMemo(() => {
    const map: Record<string, ResultChange> = {};
    for (const c of changes) if (!map[c.team_id]) map[c.team_id] = c;
    return map;
  }, [changes]);

  const sortedResults = useMemo(() => {
    const inCategory = results.filter((r) => r.category_id === selectedCategory);
    // Temps FINAL (brut + pénalités validées), DQ relégués en fin — mêmes
    // règles que la page publique (LOG-162).
    return [...inCategory].sort((a, b) => {
      const dq = (a.is_dq ? 1 : 0) - (b.is_dq ? 1 : 0);
      if (dq !== 0) return dq;
      const timeA = a.elapsed_time ?? a.real_time_seconds ?? Infinity;
      const timeB = b.elapsed_time ?? b.real_time_seconds ?? Infinity;
      return timeA - timeB;
    });
  }, [results, selectedCategory]);

  // Équipes de la catégorie sans résultat : là où l'on saisit un temps manquant.
  const teamsWithoutTime = useMemo(() => {
    const withResult = new Set(results.map((r) => r.team_id));
    return teams
      .filter((tm) => tm.category_id === selectedCategory && !withResult.has(tm.team_id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teams, results, selectedCategory]);

  const teamByCode = useMemo(() => {
    const map: Record<string, Team> = {};
    for (const tm of teams) map[tm.team_id] = tm;
    return map;
  }, [teams]);

  const categoryName = categories.find((c) => c.id === selectedCategory)?.name ?? '';

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language],
  );

  // --- Édition -------------------------------------------------------------

  const openEditor = (team: Team, current: RaceResult | null) => {
    setEditing({ team, current });
    setTimeInput('');
    setReason('');
  };

  const parsedTime = parseTime(timeInput);
  const canSave = parsedTime !== null && reason.trim().length >= 3 && !saving;

  const saveTime = async () => {
    if (!editing || !eventId || parsedTime === null) return;
    setSaving(true);
    try {
      await apiService.put(`/events/${eventId}/race-results/${encodeURIComponent(editing.team.team_id)}`, {
        real_time_seconds: parsedTime,
        reason: reason.trim(),
      });
      toast.success(t('results.timeSaved'));
      setEditing(null);
      await loadResults();
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || t('results.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const removeTime = async () => {
    if (!editing || !eventId || !editing.current) return;
    if (reason.trim().length < 3) return;
    setSaving(true);
    try {
      await apiService.delete(`/events/${eventId}/race-results/${encodeURIComponent(editing.team.team_id)}`, {
        reason: reason.trim(),
      });
      toast.success(t('results.timeRemoved'));
      setEditing(null);
      await loadResults();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('results.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // --- Historique ----------------------------------------------------------

  const originLabel = (origin: ResultChange['origin']) => {
    if (origin === 'cloud_admin') return t('results.originCloudAdmin');
    if (origin === 'organizer') return t('results.originOrganizer');
    return t('results.originSync');
  };

  const filteredChanges = useMemo(() => {
    const q = historyFilter.trim().toLowerCase();
    if (!q) return changes;
    return changes.filter((c) => c.team_name.toLowerCase().includes(q) || c.team_id.toLowerCase().includes(q));
  }, [changes, historyFilter]);

  const exportCsv = () => {
    const header = [t('results.changeDate'), t('results.changeTeam'), t('results.bib'), t('results.changeBefore'), t('results.changeAfter'), t('results.changeReason'), t('results.changeBy'), t('results.changeOrigin')];
    const escape = (v: string | number | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = filteredChanges.map((c) => [
      dateFormatter.format(new Date(c.created_at)), c.team_name, c.team_id,
      c.old_value === null ? '' : formatTime(c.old_value), c.new_value === null ? '' : formatTime(c.new_value),
      c.reason ?? '', c.changed_by ?? '', originLabel(c.origin),
    ]);
    const csv = [header, ...rows].map((r) => r.map(escape).join(';')).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historique-resultats-${eventId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Rendu ---------------------------------------------------------------

  const EditButton: React.FC<{ team: Team; current: RaceResult | null; label: string }> = ({ team, current, label }) => (
    <button
      type="button"
      onClick={() => openEditor(team, current)}
      aria-label={label}
      title={label}
      className="inline-grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-[opacity,color] duration-150 hover:text-foreground active:scale-95 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Pencil className="h-3.5 w-3.5" />
    </button>
  );

  const Annotation: React.FC<{ result: RaceResult }> = ({ result }) => {
    if (result.is_dq) return <span className="text-xs font-bold text-destructive">{t('results.dq')}</span>;
    const parts: React.ReactNode[] = [];
    if ((result.penalty_seconds ?? 0) > 0) {
      parts.push(<span key="pen" className="text-xs text-muted-foreground">{t('results.penaltyShort', { seconds: result.penalty_seconds })}</span>);
    }
    if (result.manual_override_at) {
      const last = lastChangeByTeam[result.team_id];
      parts.push(
        <Tooltip key="mark">
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-default items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
              <Pencil className="h-3 w-3" />{t('results.corrected')}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p>{t('results.correctedAt', { date: dateFormatter.format(new Date(result.manual_override_at)) })}</p>
            {last?.reason && <p className="mt-1 text-muted-foreground">{last.reason}</p>}
          </TooltipContent>
        </Tooltip>,
      );
    }
    return <span className="inline-flex items-center gap-3">{parts}</span>;
  };

  const renderRanking = () => {
    if (loading && results.length === 0) {
      return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
    }
    if (error) {
      return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
    }

    return (
      <>
        {sortedResults.length === 0 ? (
          <p className="py-8 text-sm text-muted-foreground">{t('results.noResultsForCategory')}</p>
        ) : (
          <>
            {/* Desktop : table fine, sections ouvertes, temps calés dans leur colonne */}
            <div className="hidden md:block">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    <th className="w-14 px-3 py-2.5">{t('results.rank')}</th>
                    <th className="px-3 py-2.5">{t('results.team')}</th>
                    <th className="w-32 px-3 py-2.5 text-right">{t('results.time')}</th>
                    <th className="w-40 px-3 py-2.5" />
                    <th className="w-12 px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {sortedResults.map((result, idx) => {
                    const rank = result.is_dq ? null : idx + 1;
                    const top = rank !== null && rank <= 3;
                    return (
                      <tr key={result.id} className="group border-b border-border transition-colors hover:bg-white/[0.03]">
                        <td className={cn('px-3 py-3 tabular-nums', top ? 'font-bold text-foreground' : 'text-muted-foreground')}>
                          {rank === 1 ? (
                            <span className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-400 bg-clip-text text-base font-extrabold text-transparent">{/* check-colors: ignore — dégradé de marque, rang 1 */}1</span>
                          ) : rank ?? t('results.none')}
                        </td>
                        <td className="px-3 py-3">
                          <span className={cn(top ? 'font-bold' : 'font-semibold')}>{result.team_name}</span>
                          <span className="ml-2 text-xs tabular-nums text-muted-foreground">{result.bib_number || result.team_id}</span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className={cn(DIGITAL, 'text-xl', top && 'font-bold', result.is_dq && 'text-muted-foreground line-through')}>
                            {formatTime(result.elapsed_time ?? result.real_time_seconds)}
                          </span>
                        </td>
                        <td className="px-3 py-3"><Annotation result={result} /></td>
                        <td className="px-3 py-3 text-right">
                          {teamByCode[result.team_id] && (
                            <EditButton team={teamByCode[result.team_id]} current={result} label={t('results.editTime')} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile : lignes ouvertes, crayon toujours visible (pas de survol) */}
            <div className="md:hidden">
              {sortedResults.map((result, idx) => {
                const rank = result.is_dq ? null : idx + 1;
                const top = rank !== null && rank <= 3;
                return (
                  <div key={result.id} className="group flex items-center gap-3 border-b border-border py-3">
                    <span className={cn('w-6 tabular-nums', top ? 'font-bold' : 'text-muted-foreground')}>
                      {rank === 1 ? (
                        <span className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-400 bg-clip-text font-extrabold text-transparent">{/* check-colors: ignore — dégradé de marque, rang 1 */}1</span>
                      ) : rank ?? t('results.none')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cn('truncate', top ? 'font-bold' : 'font-semibold')}>{result.team_name}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="tabular-nums">{result.bib_number || result.team_id}</span>
                        {' '}<Annotation result={result} />
                      </p>
                    </div>
                    <span className={cn(DIGITAL, 'text-lg', top && 'font-bold', result.is_dq && 'text-muted-foreground line-through')}>
                      {formatTime(result.elapsed_time ?? result.real_time_seconds)}
                    </span>
                    {teamByCode[result.team_id] && (
                      <EditButton team={teamByCode[result.team_id]} current={result} label={t('results.editTime')} />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Sans temps : là où l'on saisit un résultat manquant */}
        {teamsWithoutTime.length > 0 && (
          <section className="mt-8">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {t('results.noTimeTeams')} · {teamsWithoutTime.length}
            </h2>
            <p className="mb-1 text-sm text-muted-foreground">{t('results.noTimeTeamsHint')}</p>
            {teamsWithoutTime.map((team) => (
              <div key={team.id} className="flex items-center gap-3 border-b border-border px-3 py-2.5">
                <span className="font-semibold">{team.name}</span>
                <span className="text-xs tabular-nums text-muted-foreground">{team.team_id}</span>
                <span className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => openEditor(team, null)}>
                  <Plus className="h-4 w-4" />{t('results.addTime')}
                </Button>
              </div>
            ))}
          </section>
        )}
      </>
    );
  };

  const renderHistory = () => (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Input
          value={historyFilter}
          onChange={(e) => setHistoryFilter(e.target.value)}
          placeholder={t('results.searchTeamPlaceholder')}
          className="h-9 w-56"
        />
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={filteredChanges.length === 0}>
          <Download className="h-4 w-4" />{t('results.exportCsv')}
        </Button>
      </div>
      {filteredChanges.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">{t('results.changesEmpty')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                <th className="px-3 py-2.5">{t('results.changeDate')}</th>
                <th className="px-3 py-2.5">{t('results.changeTeam')}</th>
                <th className="px-3 py-2.5">{t('results.changeBefore')} → {t('results.changeAfter')}</th>
                <th className="px-3 py-2.5">{t('results.changeReason')}</th>
                <th className="px-3 py-2.5">{t('results.changeBy')}</th>
                <th className="px-3 py-2.5">{t('results.changeOrigin')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredChanges.map((c) => (
                <tr key={c.id} className="border-b border-border align-top">
                  <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{dateFormatter.format(new Date(c.created_at))}</td>
                  <td className="px-3 py-3"><span className="font-semibold">{c.team_name}</span> <span className="text-xs tabular-nums text-muted-foreground">{c.team_id}</span></td>
                  <td className="whitespace-nowrap px-3 py-3">
                    <span className={cn(DIGITAL, 'text-base text-muted-foreground line-through')}>{c.old_value === null ? t('results.none') : formatTime(c.old_value)}</span>
                    <span className="mx-2 text-muted-foreground">→</span>
                    <span className={cn(DIGITAL, 'text-base')}>{c.new_value === null ? t('results.none') : formatTime(c.new_value)}</span>
                  </td>
                  <td className="px-3 py-3">{c.reason || <span className="text-muted-foreground">{t('results.none')}</span>}</td>
                  <td className="whitespace-nowrap px-3 py-3">{c.changed_by || t('results.none')}</td>
                  <td className="whitespace-nowrap px-3 py-3">
                    <span className={cn('text-[11px] font-semibold uppercase tracking-wide', c.origin === 'cloud_admin' ? 'text-primary' : 'text-muted-foreground')}>
                      {originLabel(c.origin)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-bold">{t('results.title')}</h1>
        <UnderlineTabs
          aria-label={t('results.title')}
          value={view}
          onChange={(v) => setView(v as View)}
          items={[
            { value: 'ranking', label: t('results.ranking') },
            { value: 'history', label: <>{t('results.history')}{changes.length > 0 && <span className="ml-1 font-normal text-muted-foreground">· {changes.length}</span>}</> },
          ]}
        />
      </div>

      {view === 'ranking' ? (
        <>
          {categories.length > 0 && (
            <UnderlineTabs
              aria-label={t('results.category')}
              size="sm"
              scrollable
              value={selectedCategory}
              onChange={setSelectedCategory}
              items={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
          {renderRanking()}
        </>
      ) : renderHistory()}

      {/* Feuille d'édition — même grammaire que l'édition de score CrossFit sur mobile */}
      <Sheet open={editing !== null} onOpenChange={(open) => { if (!open && !saving) setEditing(null); }}>
        <SheetContent side="bottom" className="mx-auto rounded-t-2xl sm:max-w-2xl">
          {editing && (
            <>
              <SheetHeader>
                <SheetTitle>{editing.current ? t('results.editTime') : t('results.addTime')} · {editing.team.name}</SheetTitle>
                <SheetDescription>{t('results.bib')} {editing.team.team_id}{categoryName ? ` · ${categoryName}` : ''}</SheetDescription>
              </SheetHeader>

              {editing.current && (
                <div className="my-3 flex items-center justify-between border-y border-border py-2.5 text-sm text-muted-foreground">
                  <span>{t('results.currentTime')}</span>
                  <span className={cn(DIGITAL, 'text-lg text-foreground')}>{formatTime(editing.current.real_time_seconds)}</span>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="race-time">{t('results.timeInput')} <span className="text-destructive">*</span></Label>
                  <Input
                    id="race-time"
                    autoFocus
                    inputMode="numeric"
                    value={timeInput}
                    onChange={(e) => setTimeInput(e.target.value)}
                    placeholder="mm:ss"
                    aria-invalid={timeInput.length > 0 && parsedTime === null}
                    className={cn(DIGITAL, 'h-12 text-center text-2xl', timeInput.length > 0 && parsedTime === null && 'border-destructive/40')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('results.timeFormatHint')} · {parsedTime !== null
                      ? <span className="font-medium text-foreground">{formatTime(parsedTime)}</span>
                      : timeInput.length > 0 ? <span className="text-destructive">{t('results.timeInvalid')}</span> : t('results.none')}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="race-reason">{t('results.reason')} <span className="text-destructive">*</span></Label>
                  <Textarea
                    id="race-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('results.reasonPlaceholder')}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">{t('results.reasonRequired')}</p>
                </div>
              </div>

              <SheetFooter className="mt-4 flex-row items-center gap-2">
                {editing.current && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={saving || reason.trim().length < 3}
                    title={t('results.removeHint')}
                    onClick={() => void removeTime()}
                  >
                    <Trash2 className="h-4 w-4" />{t('results.removeTime')}
                  </Button>
                )}
                <span className="flex-1" />
                <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>{t('common.cancel')}</Button>
                <Button onClick={() => void saveTime()} disabled={!canSave}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default EventRaceResults;
